
import { useStitches } from 'theme';
import React, { CSSProperties, useRef, useState } from 'react';
import { Modal, ModalBody } from 'baseui/modal';
import SecuredImage from 'components/SecuredImage';
import { Button } from 'components/Button';


const ModalImageZoom = (props: any) => {
  const { css } = useStitches();
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  const parseDrawables = (value?: string) => {
    if (!value) {
      return {
        rectangles: [],
        arrows: [],
      };
    }

    const extract = (expression: RegExp) =>
      Array.from(value.matchAll(expression))
        .map((match) =>
          match[1]
            .split(',')
            .map((item) => parseFloat(item.trim()))
            .filter((item) => !Number.isNaN(item)),
        )
        .filter((shape) => shape.length === 4);

    return {
      rectangles: extract(/Rect\(([^)]*)\)/gi),
      arrows: extract(/Arrow\(([^)]*)\)/gi),
    };
  };

  const getRectangleTLBR = (rect: number[]) => {
    const rectangle = [];

    rectangle[0] = Math.min(rect[2], rect[0]);
    rectangle[2] = Math.max(rect[2], rect[0]);
    rectangle[1] = Math.min(rect[1], rect[3]);
    rectangle[3] = Math.max(rect[1], rect[3]);

    return rectangle;
  };

  const getRectangleStyle = (rect: number[]) => {
    if (!Array.isArray(rect) || rect.length < 4) {
      return {};
    }

    const rectangle = getRectangleTLBR(rect);

    const left = (rectangle[0] || 0) * 100 + '%';
    const top = (rectangle[1] || 0) * 100 + '%';
    const right = (1 - rectangle[2]) * 100 + '%';
    const bottom = (1 - rectangle[3]) * 100 + '%';

    return {
      left,
      top,
      bottom,
      right,
      borderColor: '#00ff00',
      borderStyle: 'solid',
      borderWidth: '3px',
      position: 'absolute',
      zIndex: 99,
      pointerEvents: 'none',
    };
  };

  const drawables = parseDrawables(props.image?.drawables);

  const getArrowStyle = (arrow: number[]) => {
    if (!Array.isArray(arrow) || arrow.length < 4) {
      return null;
    }

    return {
      x1: arrow[0] * 100,
      y1: arrow[1] * 100,
      x2: arrow[2] * 100,
      y2: arrow[3] * 100,
    };
  };

  const onZoomIn = () => {
    setZoomScale((prev) => Math.min(5, prev + 0.2));
  };

  const onZoomOut = () => {
    setZoomScale((prev) => Math.max(0.2, prev - 0.2));
  };

  const onRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const onFlip = () => {
    setIsFlipped((prev) => !prev);
  };

  const onToggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }

      if (viewportRef.current && viewportRef.current.requestFullscreen) {
        await viewportRef.current.requestFullscreen();
      }
    } catch (error) {
      // No-op: some browsers/environments can block fullscreen transitions.
    }
  };

  return (
    <>
      <Modal
        onClose={() => {
          props.setIsOpen(false);
          setZoomScale(1);
          setRotation(0);
          setIsFlipped(false);
        }}
        isOpen={props.isOpen}
        overrides={{
          Dialog: {
            style: {
              width: '100%',
              maxWidth: '96rem',
              height: '95vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              backgroundColor: '#000000',
            },
          },
        }}
      >
        <ModalBody
          $style={{
            marginLeft: 0,
            marginRight: 0,
            marginBottom: 0,
            marginTop: 0,
          }}
          className={css({
            overflow: 'auto',
            layout: 'row',
            flex: 1,
            backgroundColor: '#000000',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          })}
          ref={viewportRef}
        >
          <div
            className={css({
              position: 'absolute',
              top: '$scale400',
              right: '$scale400',
              zIndex: 200,
              display: 'flex',
              gap: '$scale200',
            })}
          >
            <Button size="compact" onClick={onZoomIn}>+</Button>
            <Button size="compact" onClick={onZoomOut}>-</Button>
            <Button size="compact" onClick={onRotate}>Rotate</Button>
            <Button size="compact" onClick={onFlip}>Flip</Button>
            <Button size="compact" onClick={onToggleFullscreen}>Full</Button>
          </div>
          <div
            className={css({
              position: 'relative',
              display: 'inline-block',
              backgroundColor: '#000000',
            })}
            style={{
              transform: `scaleX(${isFlipped ? -1 : 1}) scale(${zoomScale}) rotate(${rotation}deg)`,
              transformOrigin: 'center center',
            }}
            draggable={false}
          >
            <SecuredImage
              link={props.image.link}
              className={css({
                width: 'auto',
                height: 'auto',
                maxWidth: 'none',
                maxHeight: 'none',
                display: 'block',
              })}
            />
            {drawables.rectangles.map((rect, index) => (
              <div
                key={`zoom-rect-${index}`}
                style={getRectangleStyle(rect) as CSSProperties}
              />
            ))}
            <svg
              className={css({
                position: 'absolute',
                inset: 0,
                width: '$full',
                height: '$full',
                pointerEvents: 'none',
                zIndex: 100,
              })}
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <defs>
                <marker
                  id={`zoom-arrowhead-${props.image?.uuid ?? 'img'}`}
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <path d="M0,0 L0,6 L6,3 z" fill="#00ff00" />
                </marker>
              </defs>
              {drawables.arrows.map((arrow, index) => {
                const line = getArrowStyle(arrow);
                if (!line) {
                  return null;
                }
                return (
                  <line
                    key={`zoom-arrow-${index}`}
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke="#00ff00"
                    strokeWidth="0.6"
                    markerEnd={`url(#zoom-arrowhead-${props.image?.uuid ?? 'img'})`}
                  />
                );
              })}
            </svg>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ModalImageZoom;